'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface AppImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    priority?: boolean;
    quality?: number;
    placeholder?: 'blur' | 'empty';
    blurDataURL?: string;
    fill?: boolean;
    sizes?: string;
    onClick?: () => void;
    fallbackSrc?: string;
    id?: string;
    [key: string]: any;
}

// Hostnames configured in next.config.mjs remotePatterns — eligible for Next.js Image optimization
const OPTIMIZABLE_HOSTNAMES = [
    'images.unsplash.com',
    'images.pexels.com',
    'images.pixabay.com',
    'img.rocket.new',
];

function isOptimizableUrl(src: string): boolean {
    try {
        const url = new URL(src);
        return OPTIMIZABLE_HOSTNAMES.includes(url.hostname);
    } catch {
        return false;
    }
}

function AppImage({
    src,
    alt,
    width,
    height,
    className = '',
    priority = false,
    quality = 80,
    placeholder = 'empty',
    blurDataURL,
    fill = false,
    sizes,
    onClick,
    fallbackSrc = '/assets/images/no_image.png',
    id,
    ...props
}: AppImageProps) {
    const [imageSrc, setImageSrc] = useState(src);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const isExternal = imageSrc.startsWith('http://') || imageSrc.startsWith('https://');
    const isLocal = imageSrc.startsWith('/') || imageSrc.startsWith('./') || imageSrc.startsWith('data:');
    const canOptimize = isExternal && isOptimizableUrl(imageSrc);

    const handleError = () => {
        if (!hasError && imageSrc !== fallbackSrc) {
            setImageSrc(fallbackSrc);
            setHasError(true);
        }
        setIsLoading(false);
    };

    const handleLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    const loadingClass = isLoading ? 'bg-gray-200' : '';
    const cursorClass = onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : '';
    const commonClassName = `${className} ${loadingClass} ${cursorClass}`.trim();

    // Use Next.js Image for local images OR optimizable external URLs (WebP + lazy loading)
    if (isLocal || canOptimize) {
        const imageProps = {
            src: imageSrc,
            alt,
            className: commonClassName,
            priority,
            quality,
            placeholder,
            blurDataURL,
            onError: handleError,
            onLoad: handleLoad,
            onClick,
            id,
            ...props,
        };

        if (fill) {
            return (
                <Image
                    {...imageProps}
                    fill
                    sizes={sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'}
                    style={{ objectFit: 'cover' }}
                />
            );
        }

        return (
            <Image
                {...imageProps}
                width={width || 800}
                height={height || 600}
                sizes={sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'}
            />
        );
    }

    // Fallback: plain img tag for non-optimizable external URLs
    const imgStyle: React.CSSProperties = {};
    if (width) imgStyle.width = width;
    if (height) imgStyle.height = height;

    if (fill) {
        return (
            <div className={`relative ${className}`} style={{ width: width || '100%', height: height || '100%' }}>
                <img
                    src={imageSrc}
                    alt={alt}
                    id={id}
                    className={`${commonClassName} absolute inset-0 w-full h-full object-cover`}
                    onError={handleError}
                    onLoad={handleLoad}
                    onClick={onClick}
                    loading="lazy"
                    style={imgStyle}
                    {...props}
                />
            </div>
        );
    }

    return (
        <img
            src={imageSrc}
            alt={alt}
            id={id}
            className={commonClassName}
            onError={handleError}
            onLoad={handleLoad}
            onClick={onClick}
            loading="lazy"
            style={imgStyle}
            {...props}
        />
    );
}

export default AppImage;